import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError, EMPTY } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { MembershipService } from '../../services/membership/membership.service';
import { AuthService } from '../../services/auth';

// Helper function to check if URL should be allowed
function isAllowedUrl(url: string): boolean {
  const normalizedUrl = url.toLowerCase();
  return normalizedUrl.includes('/api/v1/membership/') ||
         normalizedUrl.includes('/api/v1/billing/') ||
         normalizedUrl.includes('/api/v1/payment/') ||
         normalizedUrl.includes('/api/v1/auth/');
}

export const membershipInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const router = inject(Router);
  const membershipService = inject(MembershipService);
  const authService = inject(AuthService);

  // Skip membership validation for system administrators
  if (authService.isSystemAdmin()) {
    return next(req);
  }

  // Always allow membership, billing, payment, and auth endpoints
  if (isAllowedUrl(req.url)) {
    // If this is a membership status check and it succeeds, reset the expired flag
    if (req.url.toLowerCase().includes('/api/v1/membership/status')) {
      return next(req).pipe(
        tap((event) => {
          // Reset expired flag on successful membership status check
          // HttpResponse event type is 4, but we'll reset on any successful response
          if (event.type === 4) { // HttpResponse event type
            membershipService.setExpiredMembershipLock(false);
          }
        }),
        catchError((error: HttpErrorResponse) => {
          // If status check fails with 402, set expired flag
          if (error.status === 402) {
            membershipService.setExpiredMembershipLock(true);
          }
          return throwError(() => error);
        })
      );
    }
    return next(req);
  }

  // If we already know membership is expired, block requests except to allowed endpoints
  const currentStatus = membershipService.getCurrentMembershipStatus();
  if (currentStatus?.isExpired || membershipService.hasExpiredMembershipLock()) {
    // Block the request and redirect
    router.navigate(['/settings/billing'], { replaceUrl: false });
    return EMPTY; // Cancel the request immediately
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 402) { // Payment Required
        membershipService.setExpiredMembershipLock(true);

        // Update membership service state immediately
        const expiredStatus = membershipService.getCurrentMembershipStatus();
        if (!expiredStatus || !expiredStatus.isExpired) {
          // Force update to expired status
          membershipService.getMembershipStatus().subscribe({
            next: (status) => {
              if (status.isExpired) {
                router.navigate(['/settings/billing'], { replaceUrl: false });
              } else {
                // Membership is valid now, reset flag
                membershipService.setExpiredMembershipLock(false);
              }
            },
            error: () => {
              router.navigate(['/settings/billing'], { replaceUrl: false });
            }
          });
        } else {
          // Redirect to the membership page
          router.navigate(['/settings/billing'], { replaceUrl: false });
        }

        // Return empty to stop further processing
        return EMPTY;
      }
      return throwError(() => error);
    })
  );
};
